
  import { AccountModule, AccountStore } from '@/store/account';
  import { Options, Vue } from 'vue-class-component';
  import { Prop } from 'vue-property-decorator';
  import {
    AccountConfirmation,
    ApplicationRoleType,
    CreateAccountCommand,
    CreateAccountCommandBase,
    UpdateAccountCommand,
    ReactivateAccountCommand,
    UserDto
  } from '@/apiclient/apiclient_generated';
  import ChangePassword from './ChangePassword.vue';
  import ModalComponent from '@/components/ModalComponent.vue';
  import { NotificationService } from '@/services/notification-service';
  import dateFilter from '@/filters/date-time.filter';

  @Options({
    components: {
      ModalComponent,
      ChangePassword
    }
  })
  export default class CreateUpdateAccount extends Vue {
    private accountStore: AccountModule = AccountStore;
    private df = dateFilter;

    private createEvent = 'created';
    private updateEvent = 'updated';
    private reactivateEvent = 'reactivated';

    @Prop() account: UserDto;
    isLoading = false;
    password = '';
    showChangePasswordModal = false;
    showReactivateModal = false;

    adminAccountRoles = [
      ApplicationRoleType.Customer,
      ApplicationRoleType.Employee,
      ApplicationRoleType.Administrator,
      ApplicationRoleType.NewsletterRecipient
    ];
    employeeReadOnlyAccountRoles = [ApplicationRoleType.Employee, ApplicationRoleType.Customer, ApplicationRoleType.NewsletterRecipient];
    employeeAvailableAccountRoles = [ApplicationRoleType.Customer, ApplicationRoleType.NewsletterRecipient];
    accountConfirmations = [AccountConfirmation.None, AccountConfirmation.Mail, AccountConfirmation.MailWithCredentials];

    get modalHeader() {
      return this.account.id ? 'Modify Account' : 'Create Account';
    }

    get editingCurrentUser() {
      return this.accountStore.userData.id == this.account.id;
    }

    mounted() {
      if (!this.account.id) {
        if (this.accountStore.isAdmin) {
          this.account.role = this.adminAccountRoles[0];
        } else {
          this.account.role = ApplicationRoleType.Customer;
        }

        this.account.accountConfirmationType = this.accountConfirmations[0];
      }
    }

    async submitCreateUpdateForm() {
      this.isLoading = true;
      if (this.account.id) {
        await this.processUpdate();
      } else {
        await this.processCreate();
      }
    }

    async processCreate() {
      const command = this.mapBaseAccountData<CreateAccountCommand>(this.account);
      command.password = this.password;

      const result = await this.accountStore.registerAccount(command);

      this.processResult(result);
    }

    getRequiredClass(entity) {
      return entity ? '' : 'required-field';
    }

    createRandomPassword() {
      this.password = this.generateUUID();
    }

    generateUUID() {
      // Public Domain/MIT
      var d = new Date().getTime();
      if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
        d += performance.now(); //use high-precision timer if available
      }
      var newGuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

      return newGuid;
    }

    async processUpdate() {
      const command = this.mapBaseAccountData<UpdateAccountCommand>(this.account);

      //The id is separate as it is not part of the "create" command
      command.id = this.account.id;

      const result = await this.accountStore.updateAccountAction(command);

      this.processResult(result);
    }

    cancelCreateUpdateForm() {
      this.$emit('cancel');
    }

    initChangePasswordModal() {
      this.showChangePasswordModal = true;
    }

    get isCurrentFormValid() {
      if (this.accountStore.isCustomer) {
        return this.isCustomerAccountValid;
      }

      if (this.accountStore.isEmployee) {
        return this.isEmployeeAccountValid;
      }

      if (this.accountStore.isAdmin) {
        return this.isAdminAccountValid;
      }

      return false;
    }

    get isCustomerAccountValid() {
      return this.account.name && this.account.surname && this.account.email && this.accountPassword;
    }

    get isEmployeeAccountValid() {
      return this.isCustomerAccountValid && this.account.accountConfirmationType;
    }

    get isAdminAccountValid() {
      return this.isEmployeeAccountValid && this.accountRole;
    }

    get accountRole() {
      return this.account.role;
    }

    get accountPassword() {
      return this.account.id ? this.password : true;
    }

    private mapReactivate<T extends ReactivateAccountCommand>(acc) {
      return this._map(acc) as ReactivateAccountCommand;
    }

    private mapBaseAccountData<T extends CreateAccountCommandBase>(acc) {
      return this._map(acc) as T;
    }

    private _map(acc) {
      return {
        company: acc.company,
        salutation: acc.salutation,
        name: acc.name,
        surname: acc.surname,
        street: acc.street,
        streetNumber: acc.streetNumber,
        zipCode: acc.zipCode,
        city: acc.city,
        state: acc.state,
        country: acc.country,
        email: acc.email,
        vat: acc.vat,

        isEmailConfirmed: acc.emailConfirmed,
        accountRole: acc.role,
        accountConfirmationType: acc.accountConfirmationType,

        allowSendMails: acc.allowSendMails,
        emailsRolloutDate: acc.emailsRolloutDate
      };
    }

    private emitEventByData(entity) {
      if (entity && entity.modifiedDate) {
        this.emitEventByName(this.updateEvent, entity);
      } else {
        this.emitEventByName(this.createEvent, entity);
      }
    }
    private emitEventByName(event, entity = {}) {
      this.$emit(event, entity);
    }

    private processResult(result) {
      this.isLoading = false;

      if (result.isSuccess) {
        this.emitEventByData(result.entity);
      } else {
        if (result.errors[0].includes('409')) {
          this.showReactivateModal = true;
        } else {
          NotificationService.NotifyErrors(result.errors);
        }
      }
    }

    cancelReactivateForm() {
      this.showReactivateModal = false;
    }

    private getDefaultDate(date = new Date()) {
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);
      date;

      return date;
    }

    async reactivateAcc(updateAccountDate: boolean) {
      let action;

      let command = {
        email: this.account.email
      } as ReactivateAccountCommand;

      if (!updateAccountDate) {
        action = this.accountStore.reactivateAcc(command);
      } else {
        command = this.mapReactivate<ReactivateAccountCommand>(this.account);
        command.updateAccountData = true;
        command.password = this.password;

        action = this.accountStore.reactivateAcc(command);
      }

      const result = await action;

      if (result.isSuccess) {
        this.cancelReactivateForm();
        this.emitEventByName(this.reactivateEvent, result.entity);
      }
    }
  }
